import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NotificationsService } from 'angular2-notifications';
import { AbstractControl, FormBuilder, ValidationErrors, Validators } from '@angular/forms';
import { UserService } from '../../services/user.service';
import { OfframpService } from '../../services/offramp.service';
import { Ng4LoadingSpinnerService } from 'ng4-loading-spinner';
import * as moment from 'moment';

const defaults = {
  notif: {
    timeOut: 3000,
    showProgressBar: true,
    pauseOnHover: false,
    clickToClose: true,
    maxLength: 50,
  },
};

@Component({
  selector: 'app-add-payout-topup',
  templateUrl: './add-payout-topup.component.html',
  styleUrls: ['./add-payout-topup.component.css'],
})
export class AddPayoutTopupDialog implements OnInit {
  element: any = {};
  myForm: any;
  customerList: any[] = [];
  clientQuoteList: any[] = [];
  filteredCustomerList: any[] = [];
  userSearchValue: string = '';
  isUserSelectOpen: boolean = false;
  isEditMode: boolean = false;
  recordId: string = '';
  today: Date = new Date();
  settlementStatusOptions: string[] = ['PENDING', 'COMPLETED', 'PARTIAL', 'FAILED', 'REJECTED'];
  creditStatusOptions: string[] = ['PENDING', 'PROCESSING', 'FAILED', 'REJECTED', 'COMPLETED'];

  constructor(
    public dialogRef: MatDialogRef<AddPayoutTopupDialog>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private notifSer: NotificationsService,
    public fb: FormBuilder,
    public userService: UserService,
    public offrampService: OfframpService,
    private spinnerService: Ng4LoadingSpinnerService
  ) {
    this.element = data.record || {};
    this.isEditMode = !!this.element._id;
    this.recordId = this.element._id || '';

    this.myForm = this.fb.group({
      Date: [this.element.AsOfDate ? new Date(this.element.AsOfDate) : new Date(), [Validators.required]],
      UserId: [
        {
          value: this.element.UserId ? (this.element.UserId._id || this.element.UserId) : '',
          disabled: this.isEditMode
        },
        [Validators.required]
      ],
      CreditBatchId: [{ value: this.element.CreditBatchId || 'NEXT', disabled: this.isEditMode },
      [Validators.required, Validators.pattern('^[A-Z0-9]+$')]],
      client_quote_id: [
        this.element.client_quote_id ? (this.element.client_quote_id._id || this.element.client_quote_id) : ''
      ],
      // Prefilled from the selected quote's rate below, but always editable -
      // there isn't always a matching quote, and the agreed rate can differ
      // from it anyway.
      Rate: [this.element.Rate || 0, [Validators.required, Validators.min(0)]],
      CreditAmount: [this.element.CreditAmount || 0, [Validators.required, Validators.min(1)]],
      UsedAmount: [this.element.UsedAmount || 0, [Validators.required, Validators.min(0), this.maxOtherControlValidator('CreditAmount')]],
      Currency: [this.element.Currency, [Validators.required]],
      CryptoCurrency: [this.element.CryptoCurrency || 'USDT', [Validators.required]],
      CryptoCredit: [this.element.CryptoCredit || 0, [Validators.required, Validators.min(0)]],
      CryptoReceived: [this.element.CryptoReceived || 0, [Validators.min(0), this.maxOtherControlValidator('CryptoCredit')]],
      PaymentMethod: [this.element.PaymentMethod || 'Credit', [Validators.required]],
      TxnId: [this.element.TxnId || ''],
      TxnStatus: [this.element.TxnStatus, [Validators.required]],
      SettlementStatus: [this.element.SettlementStatus || 'PENDING', [Validators.required]],
      CreditStatus: [this.element.CreditStatus || 'PENDING', [Validators.required]],
      MatchedTradeAmount: [this.element.MatchedTradeAmount || 0],
      MatchedStatus: [this.element.MatchedStatus || ''],
      Profitshare_15: [this.element.Profitshare_15 || false],
      Profitshare_20: [this.element.Profitshare_20 || false],
      Note: [this.element.Note || ''],
      SendNotification: [!this.isEditMode],
    });

    this.myForm.get('client_quote_id').valueChanges.subscribe((quoteId) => this.onClientQuoteChange(quoteId));
    this.myForm.get('Rate').valueChanges.subscribe(() => this.recalculateCreditAmount());
    this.myForm.get('CryptoCredit').valueChanges.subscribe(() => this.recalculateCreditAmount());
    this.myForm.get('UserId').valueChanges.subscribe((userId) => this.onUserChange(userId));
  }

  ngOnInit() {
    this.loadCustomerList();
    this.loadClientQuoteList(this.myForm.get('UserId').value);
  }

  // Quotes are specific to the selected client - when the user changes, the
  // previously selected quote no longer applies.
  onUserChange(userId: string) {
    this.myForm.get('client_quote_id').setValue('', { emitEvent: false });
    this.loadClientQuoteList(userId);
  }

  get isClientQuoteSelected(): boolean {
    return !!this.myForm.get('client_quote_id').value;
  }

  // Only prefills Rate when a quote is actually picked - clearing the quote
  // (or there never being one) leaves whatever rate is already there alone,
  // since that's then the admin's own directly-entered rate.
  onClientQuoteChange(quoteId: string) {
    if (!quoteId) {
      return;
    }

    const quote = this.clientQuoteList.find((q) => q._id === quoteId);
    if (quote && quote.rate) {
      this.myForm.get('Rate').setValue(quote.rate, { emitEvent: false });
    }
    this.recalculateCreditAmount();
  }

  recalculateCreditAmount() {
    const rate = parseFloat(this.myForm.get('Rate').value) || 0;
    const cryptoCredit = parseFloat(this.myForm.get('CryptoCredit').value) || 0;
    this.myForm.get('CreditAmount').setValue(rate * cryptoCredit, { emitEvent: false });
    this.myForm.get('CryptoReceived').updateValueAndValidity({ emitEvent: false });
    this.myForm.get('UsedAmount').updateValueAndValidity({ emitEvent: false });
  }

  // Caps a field (e.g. Crypto Received) at the current value of another
  // field (e.g. Credit Crypto) - equal is allowed, exceeding it is not.
  maxOtherControlValidator(otherControlName: string) {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.parent) {
        return null;
      }

      const otherControl = control.parent.get(otherControlName);
      const max = parseFloat(otherControl && otherControl.value) || 0;
      const value = parseFloat(control.value) || 0;
      return value > max ? { maxOtherControl: { max } } : null;
    };
  }

  loadClientQuoteList(userId: string) {
    if (!userId) {
      this.clientQuoteList = [];
      return;
    }

    const filter = { _csrf: this.data._csrf, limit: 10000, page: 1, client_id: userId };
    this.offrampService.getAllOfframpClientQuotes(filter).subscribe(
      (response: any) => {
        this.clientQuoteList = (response && response.data) || [];
        this.recalculateCreditAmount();
      },
      () => {
        this.notifSer.error('Error', 'Failed to load client quote list', defaults.notif);
        this.clientQuoteList = [];
      }
    );
  }

  loadCustomerList() {
    this.spinnerService.show();
    const filter = {
      _csrf: this.data._csrf,
      perpage: 10000,
      page: 1,
      search: '',
      status: 'Active',
    };

    this.offrampService.getOfframpCustomerList(filter).subscribe(
      (response: any) => {
        this.spinnerService.hide();
        if (response && response.customers && Array.isArray(response.customers)) {
          this.customerList = response.customers;
        } else if (response && Array.isArray(response)) {
          this.customerList = response;
        } else if (response && response.data && Array.isArray(response.data)) {
          this.customerList = response.data;
        } else {
          this.customerList = [];
          this.notifSer.warn('Warning', 'No customers found', defaults.notif);
        }

        this.filteredCustomerList = this.customerList;
        if (!this.isEditMode && this.customerList.length > 0 && !this.myForm.get('UserId').value) {
          this.myForm.patchValue({ UserId: this.customerList[0]._id });
        }
      },
      (error) => {
        this.spinnerService.hide();
        this.notifSer.error('Error', 'Failed to load customer list', defaults.notif);
        this.customerList = [];
        this.filteredCustomerList = [];
      }
    );
  }

  onUserSelectOpened(isOpen: boolean) {
    this.isUserSelectOpen = isOpen;
    if (isOpen) {
      this.userSearchValue = '';
      this.filteredCustomerList = this.customerList;
      setTimeout(() => {
        this.addSearchToSelectPanel();
      }, 150);
    } else {
      this.userSearchValue = '';
      this.filteredCustomerList = this.customerList;
    }
  }

  addSearchToSelectPanel() {
    const panel = document.querySelector('.user-select-panel-payout-topup');
    if (panel) {
      const existingSearch = panel.querySelector('.user-search-container');
      if (existingSearch) {
        existingSearch.remove();
      }

      const searchContainer = document.createElement('div');
      searchContainer.className = 'user-search-container';
      searchContainer.innerHTML = `
        <input type="text" placeholder="Search user..." class="user-search-input" value="${this.userSearchValue || ''}">
      `;

      const searchInput = searchContainer.querySelector('.user-search-input') as HTMLInputElement;
      if (searchInput) {
        searchInput.addEventListener('input', (e: any) => {
          this.userSearchValue = e.target.value;
          this.filterUsers(e);
        });
        searchInput.addEventListener('click', (e: any) => {
          e.stopPropagation();
        });
        searchInput.addEventListener('keydown', (e: any) => {
          e.stopPropagation();
        });
      }

      if (panel.firstChild) {
        panel.insertBefore(searchContainer, panel.firstChild);
      } else {
        panel.appendChild(searchContainer);
      }

      if (searchInput) {
        setTimeout(() => {
          searchInput.focus();
        }, 50);
      }
    }
  }

  filterUsers(event: any) {
    const searchTerm = event ? (event.target ? event.target.value : event) : this.userSearchValue;
    this.userSearchValue = searchTerm || '';

    if (!searchTerm || searchTerm.trim() === '') {
      this.filteredCustomerList = this.customerList;
      return;
    }

    const term = searchTerm.toLowerCase();
    this.filteredCustomerList = this.customerList.filter((customer) => {
      const name = (customer.name || '').toLowerCase();
      const email = (customer.email || '').toLowerCase();
      return name.includes(term) || email.includes(term);
    });
  }

  formatBatchId(event: any) {
    const input = event.target as HTMLInputElement;
    if (!input) {
      return;
    }

    const normalized = (input.value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (normalized !== input.value) {
      input.value = normalized;
    }

    this.myForm.patchValue({ BatchId: normalized });
  }

  saveTopup() {
    const { notif } = defaults;

    if (this.myForm.invalid) {
      this.notifSer.error('Error', 'Please fill all required fields', notif);
      return;
    }

    this.spinnerService.show();
    const { Date: selectedDate, ...rest } = this.myForm.getRawValue();
    const formData = {
      // Include disabled UserId and CreditBatchId when updating an existing top-up.
      ...rest,
      AsOfDate: moment(selectedDate).format('YYYY-MM-DD'),
      _csrf: this.data._csrf,
    };

    if (this.isEditMode) {
      formData._id = this.recordId;
      this.userService.updatePayoutTopup(formData).subscribe(
        (response) => {
          this.spinnerService.hide();
          if (response.status || response.success) {
            this.notifSer.success('Success', response.message || 'Topup updated successfully', notif);
            this.dialogRef.close({ success: true, data: response });
          } else {
            this.notifSer.error('Error', response.message || 'Failed to update topup', notif);
          }
        },
        (err) => {
          this.spinnerService.hide();
          this.notifSer.error('Error', (err.error && err.error.message) || 'Failed to update topup', notif);
        }
      );
    } else {
      this.userService.createPayoutTopup(formData).subscribe(
        (response) => {
          this.spinnerService.hide();
          if (response.status || response.success) {
            this.notifSer.success('Success', response.message || 'Topup created successfully', notif);
            this.dialogRef.close({ success: true, data: response });
          } else {
            this.notifSer.error('Error', response.message || 'Failed to create topup', notif);
          }
        },
        (err) => {
          this.spinnerService.hide();
          this.notifSer.error('Error', (err.error && err.error.message) || 'Failed to create topup', notif);
        }
      );
    }
  }

  onCancel() {
    this.dialogRef.close({ success: false });
  }
}



// WEBPACK FOOTER //
// ./src/app/payout-topup/add-payout-topup/add-payout-topup.component.ts